import { forwardRef, RefObject, useCallback, useImperativeHandle, useState } from 'react';
import { DispatchLeg } from '@api/Trinium/Endpoints/Dispatch/types';
import { usePatchLegsMutation } from '@api/Trinium/Endpoints/Leg/slice';
import { appendErrorAnnouncement } from '@/Infrastructure/Components/Announcements/AnnouncementSlice';
import { getApiErrorMessage } from '@api/getApiError';
import { EditableCell, resolveEditTargetRows } from '@components/DataGrid/Dispatch/types';
import RenderCell from '@components/DataGrid/RenderCell';
import { defaultSortComparator, EditableCellRef, EditSaveScope } from '@components/DataGrid/types';
import RenderHeader from '@components/DataGrid/RenderHeader';
import TextField from '@forms/Components/TextInput';
import { applyColumnQuickFilter, caseInsensitiveIncludes } from '../columns';
import { GridColDefExtended } from '@mui/x-data-grid-pro';

import styles from '../../RenderCell.module.css';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import { useAppDispatch, useAppSelector } from '@reduxStore';

const headerDisplayName = 'Release #';

const releaseNumberDefinition: GridColDefExtended<DispatchLeg> = {
  field: 'Release Number',
  minWidth: 130,
  width: 150,
  headerName: 'Release Number',
  headerDisplayName,
  renderHeader: () => <RenderHeader primaryText={headerDisplayName} />,
  renderCell: ({ row }) => (
    <RenderCell
      editable
      primarySlot={
        row.relNo && (
          <span
            className={styles.bold}
            title={row.relNo}>
            {row.relNo}
          </span>
        )
      }
    />
  ),
  disableReorder: true,
  getApplyQuickFilterFn: (value: string) => {
    return (_, row): boolean =>
      applyColumnQuickFilter(value, row, (value: string, row: DispatchLeg) => {
        const { relNo } = row;
        return caseInsensitiveIncludes(value, [relNo]);
      });
  },
  valueGetter: (_, row: DispatchLeg) => row.relNo ?? '',
  sortable: true,
  sortComparator: defaultSortComparator,
  renderEditableCell: (row: DispatchLeg, ref: RefObject<EditableCellRef | null>, selectedRows?: DispatchLeg[]) => (
    <ReleaseNumberEditCell
      row={row}
      selectedRows={selectedRows}
      ref={ref}
    />
  )
};

/**
 * Component to render when the Release Number cell is clicked and enters edit mode
 */
const ReleaseNumberEditCell = forwardRef<EditableCellRef, EditableCell<DispatchLeg>>(({ row, selectedRows }, ref) => {
  const appDispatch = useAppDispatch();
  const { number: terminalNumber } = useCurrentTerminal();
  const [patchLegs] = usePatchLegsMutation();
  const [relNoVal, setRelNoVal] = useState(row.relNo);
  const isLoading = useAppSelector((state) => state.dispatchBoardState.loadingSet.includes(row.legId));

  const saveHandler = useCallback(async (scope: EditSaveScope) => {
    // Only need to call API if they changed the value
    if (relNoVal !== row.relNo) {
      const targetRows = resolveEditTargetRows(scope, row, selectedRows);

      await patchLegs({
        terminalNumber,
        legIds: targetRows.map((targetRow) => targetRow.legId),
        leg: {
          relNo: relNoVal
        }
      })
        .unwrap()
        .then((response) => {
          response.errors.forEach((error) => {
            appDispatch(appendErrorAnnouncement({ message: error.errorMessage }));
          });
        })
        .catch((error: unknown) => {
          appDispatch(appendErrorAnnouncement({ message: getApiErrorMessage(error) }));
        });
    }
  }, [appDispatch, terminalNumber, patchLegs, relNoVal, row, selectedRows]);

  /** Define the callable Ref functions */
  useImperativeHandle(ref, () => {
    return {
      saveChanges: saveHandler
    };
  }, [saveHandler]);

  return (
    <TextField
      variant='standard'
      value={relNoVal}
      onChange={(event) => {
        setRelNoVal(event.target.value);
      }}
      focused
      disabled={isLoading}
      customProps={{
        labelText: 'Release #',
        maxLength: 40,
        isDirty: false,
        errorText: undefined,
        onClear: () => {
          setRelNoVal('');
        },
        disabledReason: isLoading ? 'Updating...' : undefined
      }}
    />
  );
});

export default releaseNumberDefinition;
